import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Bot, Play, Radio, Save, Trash2, Wrench, Zap } from "lucide-react";
import * as React from "react";

import { GenerationFields, fromDraft, toDraft } from "@/components/authoring/generation-params";
import { SaveVersionDialog } from "@/components/authoring/save-version-dialog";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
  selectClass,
} from "@/components/ui/primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toaster";
import { ApiError } from "@/lib/api/client";
import {
  getVersionLlm,
  getVersionLlmHealth,
  listMyRuns,
  listPricing,
  listVersions,
} from "@/lib/api/endpoints";
import {
  isStubHandoff,
  startGovernedStream,
  startSkillStream,
  subscribeRun,
} from "@/lib/api/governed-stream";
import { getBundle, getRun } from "@/lib/api/playground";
import { formatTokens, formatUsd, sumCosts } from "@/lib/format-usage";
import { qk } from "@/lib/query/keys";
import { formatDate } from "@/lib/utils";
import type { AgentBundle, CacheDetail, GenerationParams, RunEnvelope } from "@/types/api";

const DEFAULT_MODEL = "gpt-4o-mini";

type Msg = { role: "user" | "assistant"; content: string };

/**
 * An agent's Test tab (formerly the Playground page): inspect a version's
 * governed configuration and run chat / skill turns against the live runtime.
 *
 * The agent is fixed by the page it sits on; only the version is chosen here.
 */
export function AgentTester({ slug }: { slug: string }) {
  const [label, setLabel] = React.useState("");

  const versions = useQuery({
    queryKey: qk.versions(slug),
    queryFn: () => listVersions(slug),
  });

  // Prefer the version tagged `latest` — that is what an operator means by
  // "the current one" — else the first listed.
  React.useEffect(() => {
    const list = versions.data;
    if (!list?.length) {
      setLabel("");
      return;
    }
    if (list.some((v) => v.version === label || v.tag === label)) return;
    const tagged = list.find((v) => v.tag === "latest");
    setLabel(tagged ? tagged.tag! : list[0]!.version);
  }, [versions.data, label]);

  const ready = !!label;
  const bundle = useQuery({
    queryKey: ["pg", "bundle", slug, label],
    queryFn: () => getBundle(slug, label),
    enabled: ready,
    retry: false,
  });

  if (versions.isSuccess && versions.data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This agent has no versions yet. Create one on the Versions tab to test it.
      </p>
    );
  }

  return (
    <div>
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="w-56 space-y-1.5">
            <Label htmlFor="pg-label">Version</Label>
            <select
              id="pg-label"
              className={selectClass}
              value={label}
              disabled={!versions.data?.length}
              onChange={(e) => setLabel(e.target.value)}
            >
              {versions.isLoading ? (
                <option value="">Loading…</option>
              ) : (
                versions.data?.map((v) => (
                  <option key={v.id} value={v.tag ?? v.version}>
                    {v.version}
                    {v.tag ? ` (${v.tag})` : ""} · {v.status}
                  </option>
                ))
              )}
            </select>
          </div>
        </CardContent>
      </Card>

      {bundle.isError ? (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="pt-6 text-sm">
            <span className="font-medium text-destructive">Could not load agent: </span>
            {(bundle.error as ApiError)?.message ?? "unknown error"}
          </CardContent>
        </Card>
      ) : (
        <>
          <StatusStrip bundle={bundle.data} slug={slug} label={label} />
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <BundlePanel bundle={bundle.data} loading={bundle.isLoading} />
            <div className="space-y-6">
              <Runner
                bundle={bundle.data}
                slug={slug}
                label={label}
                // Land on the new draft so the next turns run what was saved.
                onVersionSaved={setLabel}
              />
              <RunsPanel />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---- #6 status strip ------------------------------------------------------

function StatusStrip({
  bundle,
  slug,
  label,
}: {
  bundle?: AgentBundle;
  slug: string;
  label: string;
}) {
  const health = useQuery({
    queryKey: ["pg", "health", slug, label],
    queryFn: () => getVersionLlmHealth(slug, label),
    retry: false,
    enabled: !!slug && !!label,
    refetchOnWindowFocus: false,
  });

  const caps = bundle?.capabilities ?? [];
  const mcpCount = caps.filter((c) => c.execution_type === "mcp").length;

  const llmDot = health.data
    ? health.data.reachable === true
      ? "bg-emerald-500"
      : health.data.reachable === false
        ? "bg-destructive"
        : "bg-muted-foreground"
    : "bg-muted-foreground";

  return (
    <div className="mb-6 flex flex-wrap items-center gap-2">
      <Pill icon={<Activity className="h-3.5 w-3.5" />}>
        <span className={`h-2 w-2 rounded-full ${llmDot}`} />
        LLM
        {health.data ? (
          <span className="font-mono text-xs">
            {health.data.provider}/{health.data.model}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">…</span>
        )}
        {health.data?.latency_ms != null ? (
          <span className="text-xs text-muted-foreground">
            {Math.round(health.data.latency_ms)}ms
          </span>
        ) : null}
        {health.data ? (
          <Badge variant="secondary" className="text-[10px]">
            {health.data.key_source === "version"
              ? "version key"
              : health.data.key_source === "tenant"
                ? "tenant key"
                : "no key"}
          </Badge>
        ) : null}
      </Pill>
      <Pill icon={<Wrench className="h-3.5 w-3.5" />}>
        Tools <span className="font-mono text-xs">{caps.length}</span>
      </Pill>
      <Pill icon={<Radio className="h-3.5 w-3.5" />}>
        MCP <span className="font-mono text-xs">{mcpCount}</span>
      </Pill>
    </div>
  );
}

function Pill({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm">
      <span className="text-muted-foreground">{icon}</span>
      {children}
    </span>
  );
}

// ---- bundle panel (unchanged) ---------------------------------------------

function BundlePanel({ bundle, loading }: { bundle?: AgentBundle; loading: boolean }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <Bot className="h-5 w-5 text-primary" />
        <CardTitle className="text-base">Agent configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : !bundle ? (
          <p className="text-sm text-muted-foreground">Load an agent to inspect it.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold">{bundle.name}</span>
              <Badge variant="secondary">v{bundle.version}</Badge>
              {bundle.tag ? <Badge variant="outline">{bundle.tag}</Badge> : null}
              {bundle.supports_chat ? <Badge variant="success">chat</Badge> : null}
            </div>
            {bundle.description ? (
              <p className="text-sm text-muted-foreground">{bundle.description}</p>
            ) : null}
            <Field label="Identity" value={bundle.soul} />
            <Field label="Prompt Instructions" value={bundle.instructions} />
            <Field label="Communication Instructions" value={bundle.chat_instructions} />
            <div>
              <SectionLabel>Skills ({bundle.skills.length})</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {bundle.skills.length === 0 ? (
                  <span className="text-sm text-muted-foreground">None</span>
                ) : (
                  bundle.skills.map((s) => (
                    <Badge key={s.slug} variant="outline" className="font-mono">
                      {s.slug}
                    </Badge>
                  ))
                )}
              </div>
            </div>
            <div>
              <SectionLabel>Capabilities ({bundle.capabilities.length})</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {bundle.capabilities.length === 0 ? (
                  <span className="text-sm text-muted-foreground">None</span>
                ) : (
                  bundle.capabilities.map((c) => (
                    <Badge key={c.tool_name} variant="outline" className="font-mono">
                      {c.tool_name}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---- runner: history (#2) + streaming (#5) + estimate (#3) ----------------

function estTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/** What one finished turn actually cost, read from its audit row. */
interface TurnUsage {
  tokensIn: number | null;
  tokensOut: number | null;
  llmCost: number | null;
  /** Completions a SQL knowledge base made to write its queries. */
  toolTokensIn: number;
  toolTokensOut: number;
  toolCost: number | null;
  embeddingTokens: number;
  embeddingCost: number | null;
  cacheHit: boolean | null;
  cacheDetail: CacheDetail | null;
}

const num = (v: unknown): number | null => (typeof v === "number" ? v : null);

/** Null until the audit row exists. The checkpoint's envelope arrives first and
 * carries no usage — `embedding_tokens` is only on the audit summary. */
function turnUsage(env: Record<string, unknown> | null | undefined): TurnUsage | null {
  if (!env || !("embedding_tokens" in env)) return null;
  const detail = env.cache_detail;
  return {
    tokensIn: num(env.tokens_prompt),
    tokensOut: num(env.tokens_completion),
    llmCost: num(env.cost_usd),
    toolTokensIn: num(env.tool_llm_tokens_prompt) ?? 0,
    toolTokensOut: num(env.tool_llm_tokens_completion) ?? 0,
    toolCost: num(env.tool_llm_cost_usd),
    embeddingTokens: num(env.embedding_tokens) ?? 0,
    embeddingCost: num(env.embedding_cost_usd),
    cacheHit: typeof env.cache_hit === "boolean" ? env.cache_hit : null,
    cacheDetail: detail && typeof detail === "object" ? (detail as CacheDetail) : null,
  };
}

/** One line for the cache's verdict — used for the live `semantic_cache`
 * event and, if that event was missed, rebuilt from the run record. */
function cacheVerdictLine(p: Record<string, unknown>): string {
  const score = typeof p.score === "number" ? p.score.toFixed(3) : null;
  const threshold = typeof p.threshold === "number" ? String(p.threshold) : "?";
  if (p.hit) {
    const from =
      typeof p.source_run_id === "string"
        ? ` · reusing the answer from run ${p.source_run_id.slice(0, 8)}`
        : "";
    return `⚡ semantic cache HIT — similarity ${score ?? "?"} ≥ ${threshold}${from}`;
  }
  if (p.error) return "○ semantic cache unavailable → asking the model";
  if (score) return `○ semantic cache miss — best match ${score} < ${threshold} → asking the model`;
  return "○ semantic cache miss — no stored answer yet → asking the model";
}

/** What the run log should say about the cache once the turn is over. */
function cacheLogTail(
  detail: CacheDetail,
  sawVerdict: boolean,
): { head: string[]; tail: string[] } {
  const head = sawVerdict ? [] : [cacheVerdictLine(detail as Record<string, unknown>)];
  const tail = detail.hit
    ? ["↳ answered from cache — no LLM call, no knowledge-base search, no tool calls this turn"]
    : detail.stored
      ? ["↳ answer stored in the semantic cache for the next similar question"]
      : [];
  return { head, tail };
}

/** Finish a turn from the run record when the channel does not.
 *
 * A semantic-cache hit can complete a few hundred milliseconds after the 202 —
 * before the browser has even loaded Ably and subscribed — so its `completed`
 * event is published to nobody. Without this the Playground would sit on
 * "Streaming…" for an answer that already exists. Returns a cancel function. */
function watchForCompletion(
  runId: string,
  onTerminal: (kind: string, text: string) => void,
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const startedAt = Date.now();
  const tick = async () => {
    if (stopped) return;
    try {
      const run = await getRun(runId);
      // `final_state` only comes with the audit row, which is also what the
      // usage read-out needs — wait for that rather than the bare checkpoint.
      if (run.is_terminal && run.final_state) {
        const text = run.final_state.final_response;
        onTerminal(`run_${run.phase}`, typeof text === "string" ? text : "");
        return;
      }
    } catch {
      // Not readable yet; keep polling.
    }
    if (!stopped && Date.now() - startedAt < 120_000) timer = setTimeout(() => void tick(), 1500);
  };
  timer = setTimeout(() => void tick(), 1500);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

/** The run completes on the channel a moment before its audit row commits,
 * so try a few times rather than showing nothing. */
async function fetchTurnUsage(runId: string): Promise<TurnUsage | null> {
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const usage = turnUsage((await getRun(runId)).envelope);
      if (usage) return usage;
    } catch {
      // Not readable yet; retry below.
    }
    await new Promise((r) => setTimeout(r, 600));
  }
  return null;
}

/** Compact one-line label for a governed run lifecycle event. */
function formatEvent(kind: string, payload: Record<string, unknown>): string {
  const name = typeof payload.name === "string" ? payload.name : "";
  switch (kind) {
    case "phase_started":
      return `→ ${payload.phase ?? "phase"}`;
    case "tool_call_started":
      return `🔧 ${name || "tool"} …`;
    case "tool_call_completed":
      return `✓ ${name || "tool"}`;
    case "tool_call_failed":
      return `✗ ${name || "tool"} failed`;
    case "tool_call_requires_approval":
      return `⏸ ${name || "tool"} needs approval`;
    case "validation_rejected":
      return `⚠ validator ${payload.validator ?? ""} rejected`;
    case "semantic_cache":
      return cacheVerdictLine(payload);
    case "run_completed":
      return "✓ run completed";
    case "run_failed":
      return `✗ run failed${payload.reason ? ` (${payload.reason})` : ""}`;
    case "run_interrupted":
      return "⏸ run interrupted";
    default:
      return kind;
  }
}

function Runner({
  bundle,
  slug,
  label,
  onVersionSaved,
}: {
  bundle?: AgentBundle;
  slug: string;
  label: string;
  onVersionSaved: (version: string) => void;
}) {
  const qc = useQueryClient();
  const [message, setMessage] = React.useState("What can you help me with?");
  const [history, setHistory] = React.useState<Msg[]>([]);
  // Tools + validators on or off. Off is the preview: the same governed loop,
  // same transport, same audit row — just nothing for the model to call.
  //
  // Defaults to ON. It used to default to off, which made a correctly wired
  // agent look broken: attach a knowledge base, ask it a question, and the
  // model answers from its own weights because it was never handed `kb_*`.
  // The Playground's job is to show what the agent does in production, so the
  // production path is the default and prompt-only is the deliberate opt-out.
  const [useTools, setUseTools] = React.useState(true);
  // One-run sampling override. Off = the version's saved params, which is what
  // production runs use; on = these fields layered over them for this run.
  const [overrideGen, setOverrideGen] = React.useState(false);
  const [genDraft, setGenDraft] = React.useState(() => toDraft(null));
  // "Save as new version" (AVI-166): the tuned params, parsed once at click.
  const [saveParams, setSaveParams] = React.useState<GenerationParams | null | undefined>(
    undefined,
  );
  const [streaming, setStreaming] = React.useState(false);
  const [liveText, setLiveText] = React.useState("");
  const [events, setEvents] = React.useState<string[]>([]);
  const [runError, setRunError] = React.useState<string | null>(null);
  const [lastUsage, setLastUsage] = React.useState<TurnUsage | null>(null);
  const [skillSlug, setSkillSlug] = React.useState("");
  const [skillInput, setSkillInput] = React.useState("{\n  \n}");
  // Defer the skill to the job queue instead of blocking the request.
  // Off by default: the sync path is the frozen /api/v1 route, and short
  // skills are better served by it — no queue hop, answer in the response.
  const [skillEvents, setSkillEvents] = React.useState<string[]>([]);
  const [skillRunning, setSkillRunning] = React.useState(false);
  const [skillResult, setSkillResult] = React.useState<RunEnvelope | undefined>(undefined);
  // The skill's answer. It is NOT in the audit envelope (that is cost and
  // phase) and NOT on the Ably channel (that is lifecycle) — it lives in the
  // run's final state, which is exactly where a deferred run should keep it.
  const [skillOutput, setSkillOutput] = React.useState<Record<string, unknown> | null>(null);
  const [skillError, setSkillError] = React.useState<string | null>(null);
  // Cleanup for an in-flight Ably subscription on the skill tab.
  const skillCleanup = React.useRef<null | (() => void)>(null);
  // Cleanup for an in-flight Ably subscription (governed streaming).
  const govCleanup = React.useRef<null | (() => void)>(null);
  React.useEffect(() => () => govCleanup.current?.(), []);
  // Cancels the run-record poll that backs up the channel (see watchForCompletion).
  const watchCleanup = React.useRef<null | (() => void)>(null);
  React.useEffect(() => () => watchCleanup.current?.(), []);
  React.useEffect(() => () => skillCleanup.current?.(), []);

  const llm = useQuery({
    queryKey: ["pg", "vllm", slug, label],
    queryFn: () => getVersionLlm(slug, label),
    retry: false,
    enabled: !!slug && !!label,
  });
  const pricing = useQuery({ queryKey: ["pg", "pricing"], queryFn: listPricing });
  const model = llm.data?.model || DEFAULT_MODEL;
  const provider = llm.data?.provider ?? "openai";
  const priceForModel = pricing.data?.find((p) => p.id === model);

  // Reset the transcript when the loaded agent/version changes.
  const targetKey = `${slug}/${label}`;
  const loadedFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (loadedFor.current !== targetKey) {
      loadedFor.current = targetKey;
      setHistory([]);
      setLiveText("");
      setRunError(null);
    }
  }, [targetKey]);

  React.useEffect(() => {
    if (bundle?.skills[0]?.slug && !skillSlug) setSkillSlug(bundle.skills[0].slug);
  }, [bundle, skillSlug]);

  const afterRun = () => qc.invalidateQueries({ queryKey: ["pg", "runs"] });

  const send = async () => {
    const text = message.trim();
    if (!text || streaming) return;
    const turns = [...history, { role: "user" as const, content: text }];
    setHistory(turns);
    setMessage("");
    setRunError(null);
    setEvents([]);
    setLastUsage(null);

    // The only execution path: the governed loop over Ably. There used to be a
    // synchronous alternative behind a "Stream tokens" toggle, which POSTed to
    // the frozen runtime through the `/pg` BFF. ADR-018 retired it rather than
    // porting it to the admin surface — it raced the invocation deadline
    // (ADR-013) and was a second code path for the same job. `useTools` decides
    // whether the model gets tools + validators; everything else is identical.
    setStreaming(true);
    setLiveText("");
    let acc = "";
    try {
      let generation = null;
      if (overrideGen) {
        const parsed = fromDraft(genDraft);
        if ("error" in parsed) throw new Error(parsed.error);
        generation = parsed.params;
      }
      const handoff = await startGovernedStream(slug, label, turns, !useTools, generation);
      if (isStubHandoff(handoff)) {
        setRunError(
          "Live streaming needs ABLY_API_KEY on the server. The run still executed — see Recent runs for its audit + cost.",
        );
        setStreaming(false);
        afterRun();
        return;
      }
      // The turn ends exactly once — from the channel, or from the run record
      // when the channel never delivered (a fast cache hit can finish before
      // the browser subscribes).
      let finished = false;
      let sawCacheVerdict = false;
      const finish = (kind: string, payload: Record<string, unknown>, text: string) => {
        if (finished) return;
        finished = true;
        watchCleanup.current?.();
        govCleanup.current?.();
        setHistory((h) => [
          ...h,
          { role: "assistant", content: text || "(no text streamed — see audit)" },
        ]);
        setLiveText("");
        setStreaming(false);
        setEvents((e) => [...e, formatEvent(kind, payload)]);
        if (kind !== "run_completed") setRunError(`Run ${kind.replace("run_", "")}.`);
        void fetchTurnUsage(handoff.run_id).then((usage) => {
          setLastUsage(usage);
          if (usage?.cacheDetail) {
            const { head, tail } = cacheLogTail(usage.cacheDetail, sawCacheVerdict);
            setEvents((e) => [...head, ...e, ...tail]);
          }
          afterRun();
        });
      };
      watchCleanup.current = watchForCompletion(handoff.run_id, (kind, text) =>
        finish(kind, {}, acc || text),
      );
      govCleanup.current = subscribeRun(handoff, {
        onToken: (t) => {
          acc += t;
          setLiveText(acc);
        },
        onEvent: (kind, payload) => {
          if (kind === "semantic_cache") sawCacheVerdict = true;
          setEvents((e) => [...e, formatEvent(kind, payload)]);
        },
        onDone: (kind, payload) => finish(kind, payload, acc),
        onError: (msg) => {
          // Live updates failed, but the run is still executing: say so and
          // let the run-record poll deliver the answer.
          if (finished) return;
          setRunError(`${msg} Waiting for the run to finish…`);
        },
      });
    } catch (e) {
      setRunError(e instanceof ApiError ? e.message : "Failed to start the streamed run");
      setStreaming(false);
    }
  };

  const runSkillDeferred = async () => {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(skillInput || "{}");
    } catch {
      setSkillError("Input is not valid JSON");
      return;
    }
    setSkillRunning(true);
    setSkillEvents([]);
    setSkillResult(undefined);
    setSkillOutput(null);
    setSkillError(null);
    try {
      const handoff = await startSkillStream(slug, label, skillSlug, parsed);
      if (isStubHandoff(handoff)) {
        // No ABLY_API_KEY: the run is still executing, we just cannot watch it.
        // Say so rather than spinning forever on a channel nobody will publish to.
        setSkillError(
          "Live progress needs ABLY_API_KEY on the server. The skill is still running — see Recent runs.",
        );
        setSkillRunning(false);
        afterRun();
        return;
      }
      skillCleanup.current = subscribeRun(handoff, {
        // A skill emits no assistant tokens worth showing — its answer is the
        // JSON payload, fetched below once the run is terminal.
        onToken: () => {},
        onEvent: (kind, payload) => setSkillEvents((e) => [...e, formatEvent(kind, payload)]),
        onDone: async (kind, payload) => {
          setSkillEvents((e) => [...e, formatEvent(kind, payload)]);
          setSkillRunning(false);
          if (kind !== "run_completed") setSkillError(`Run ${kind.replace("run_", "")}.`);
          try {
            // The channel carries lifecycle, not the answer. Read the answer
            // from the run itself now that it is durable.
            const run = await getRun(handoff.run_id);
            if (run.envelope) setSkillResult(run.envelope);
            if (run.final_state?.skill_output) setSkillOutput(run.final_state.skill_output);
          } catch {
            setSkillError("Run finished but its result could not be loaded — see Recent runs.");
          }
          afterRun();
        },
        onError: (msg) => {
          setSkillError(msg);
          setSkillRunning(false);
        },
      });
    } catch (e) {
      setSkillError(e instanceof ApiError ? e.message : "Failed to start the skill run");
      setSkillRunning(false);
    }
  };

  // #3 pre-run estimate: input tokens from prompt + history + message.
  const promptText =
    (bundle?.soul ?? "") +
    (bundle?.instructions ?? "") +
    history.map((m) => m.content).join("") +
    message;
  const estIn = estTokens(promptText);
  const estCost = priceForModel ? (estIn / 1_000_000) * priceForModel.input_per_1m : null;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Run</CardTitle>
        {history.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setHistory([]);
              setLiveText("");
              setRunError(null);
            }}
          >
            <Trash2 className="h-4 w-4" /> Clear
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="chat">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="skill">Skill</TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="space-y-3">
            <Transcript history={history} live={streaming ? liveText : null} error={runError} />
            <div className="space-y-1.5">
              <Label htmlFor="pg-msg">Message</Label>
              <Textarea
                id="pg-msg"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
              />
            </div>
            <ModelField model={model} provider={provider} configured={!!llm.data?.model} />
            {overrideGen ? (
              <div className="rounded-md border border-border p-3">
                <GenerationFields
                  draft={genDraft}
                  onChange={setGenDraft}
                  provider={provider}
                  compact
                />
                <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
                  <span className="text-xs text-muted-foreground">
                    Happy with these settings? Keep them as a new draft version.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={streaming || !bundle}
                    onClick={() => {
                      const parsed = fromDraft(genDraft);
                      if ("error" in parsed) {
                        toast.error(parsed.error);
                        return;
                      }
                      setSaveParams(parsed.params);
                    }}
                  >
                    <Save className="h-4 w-4" />
                    Save as new version
                  </Button>
                </div>
              </div>
            ) : null}
            {bundle ? (
              <SaveVersionDialog
                open={saveParams !== undefined}
                onOpenChange={(open) => {
                  if (!open) setSaveParams(undefined);
                }}
                agentSlug={slug}
                fromLabel={label}
                fromVersion={bundle.version}
                generationParams={saveParams ?? null}
                onSaved={(v) => {
                  setOverrideGen(false);
                  onVersionSaved(v.version);
                }}
              />
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-primary"
                    checked={useTools}
                    onChange={(e) => setUseTools(e.target.checked)}
                  />
                  Tools + validators
                  <span className="text-xs text-muted-foreground">
                    {useTools
                      ? "the real runtime path — skills, capabilities, knowledge bases"
                      : "preview — prompt only, no tools for the model to call"}
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-primary"
                    checked={overrideGen}
                    onChange={(e) => setOverrideGen(e.target.checked)}
                  />
                  Override generation
                  <span className="text-xs text-muted-foreground">
                    {overrideGen ? "this run only" : "using the version's saved settings"}
                  </span>
                </label>
              </div>
              <span className="text-xs text-muted-foreground">
                ~{estIn.toLocaleString()} in-tokens
                {estCost != null ? ` · ~$${estCost.toFixed(5)}` : ""}
              </span>
            </div>
            {streaming || events.length > 0 ? (
              <EventsFeed events={events} live={streaming} />
            ) : null}
            {lastUsage ? <TurnUsageStrip usage={lastUsage} /> : null}
            <Button disabled={streaming || !message.trim()} onClick={() => void send()}>
              <Play className="h-4 w-4" />
              {streaming ? "Streaming…" : "Stream chat"}
            </Button>
          </TabsContent>

          <TabsContent value="skill" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="pg-skill">Skill slug</Label>
                <Input
                  id="pg-skill"
                  value={skillSlug}
                  onChange={(e) => setSkillSlug(e.target.value)}
                  placeholder="summarise"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pg-input">Input (JSON)</Label>
              <Textarea
                id="pg-input"
                className="font-mono text-xs"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
              />
            </div>
            <ModelField model={model} provider={provider} configured={!!llm.data?.model} />
            <p className="text-xs text-muted-foreground">
              Runs deferred: a 202 handoff plus Ably, so a long skill is not racing the invocation
              deadline (ADR-013). The synchronous mode this used to offer went away with the `/pg`
              BFF (ADR-018).
            </p>
            <Button
              disabled={skillRunning || !skillSlug.trim()}
              onClick={() => void runSkillDeferred()}
            >
              <Play className="h-4 w-4" />
              {skillRunning ? "Running…" : "Run skill"}
            </Button>
            {skillRunning || skillEvents.length > 0 ? (
              <EventsFeed events={skillEvents} live={skillRunning} />
            ) : null}
            <ResultPanel
              pending={skillRunning}
              data={skillResult}
              output={skillOutput}
              error={skillError}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function Transcript({
  history,
  live,
  error,
}: {
  history: Msg[];
  live: string | null;
  error: string | null;
}) {
  if (history.length === 0 && live === null && !error) return null;
  return (
    <div className="max-h-72 space-y-2 overflow-auto rounded-md border border-border bg-muted/20 p-3">
      {history.map((m, i) => (
        <Bubble key={i} role={m.role} text={m.content} />
      ))}
      {live !== null ? <Bubble role="assistant" text={live || "…"} /> : null}
      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm">
          <span className="font-medium text-destructive">Error: </span>
          {error}
        </div>
      ) : null}
    </div>
  );
}

function Bubble({ role, text }: { role: "user" | "assistant"; text: string }) {
  const mine = role === "user";
  return (
    <div className={mine ? "flex justify-end" : "flex justify-start"}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-md px-3 py-2 text-sm ${
          mine ? "bg-primary text-primary-foreground" : "border border-border bg-card"
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function EventsFeed({ events, live }: { events: string[]; live: boolean }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-2">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Activity className="h-3.5 w-3.5" />
        Governed run {live ? "(live)" : ""}
      </div>
      {events.length === 0 ? (
        <p className="text-xs text-muted-foreground">Waiting for the loop…</p>
      ) : (
        <ul className="space-y-0.5 font-mono text-xs">
          {events.map((e, i) => (
            <li key={i} className="text-muted-foreground">
              {e}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The last turn's real spend — LLM and embeddings side by side, since they
 * are billed on different models — and whether the semantic cache served it. */
function TurnUsageStrip({ usage }: { usage: TurnUsage }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">Last turn</span>
      {usage.cacheHit ? (
        <Badge variant="success" className="gap-1">
          <Zap className="h-3 w-3" /> served from semantic cache
        </Badge>
      ) : null}
      <span>
        LLM {formatTokens(usage.tokensIn)} in / {formatTokens(usage.tokensOut)} out ·{" "}
        <span className="tabular-nums text-foreground">{formatUsd(usage.llmCost)}</span>
      </span>
      {usage.toolTokensIn + usage.toolTokensOut > 0 ? (
        <span title="Tokens a SQL knowledge base spent writing its queries">
          KB SQL {formatTokens(usage.toolTokensIn)} in / {formatTokens(usage.toolTokensOut)} out ·{" "}
          <span className="tabular-nums text-foreground">{formatUsd(usage.toolCost)}</span>
        </span>
      ) : null}
      <span>
        Embeddings {formatTokens(usage.embeddingTokens)} tok ·{" "}
        <span className="tabular-nums text-foreground">{formatUsd(usage.embeddingCost)}</span>
      </span>
      <span>
        Total{" "}
        <span className="font-medium tabular-nums text-foreground">
          {formatUsd(sumCosts(usage.llmCost, usage.toolCost, usage.embeddingCost))}
        </span>
      </span>
    </div>
  );
}

function ModelField({
  model,
  provider,
  configured,
}: {
  model: string;
  provider: string;
  configured: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label>Model</Label>
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <Badge variant="outline" className="font-mono">
          {provider}
        </Badge>
        <span className="font-mono">{model}</span>
        <span className="text-xs text-muted-foreground">
          {configured ? "from this version" : "default — set one on the version"}
        </span>
      </div>
    </div>
  );
}

function ResultPanel({
  pending,
  data,
  error,
  output,
}: {
  pending: boolean;
  data?: RunEnvelope;
  error: unknown;
  /** A skill's parsed output, when the caller already has it.
   *
   * The deferred path does: its envelope is the audit summary (phase, tokens,
   * cost), and the answer comes from the run's final state. The sync path
   * doesn't — its envelope carries `output` directly, so this stays undefined
   * and `pickOutput` finds it below. Either way one panel renders it. */
  output?: Record<string, unknown> | null;
}) {
  if (pending) return <Skeleton className="mt-2 h-24 w-full" />;
  if (error) {
    const msg = error instanceof ApiError ? error.message : "Run failed";
    return (
      <div className="mt-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
        <span className="font-medium text-destructive">Runtime error: </span>
        {msg}
      </div>
    );
  }
  if (!data && !output) return null;
  const text = data ? pickText(data) : null;
  const skillOutput = output ?? (data ? pickOutput(data) : null);
  const failure = data ? pickFailure(data) : null;
  return (
    <div className="mt-2 space-y-2">
      {text ? (
        <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-sm">
          {text}
        </div>
      ) : null}
      {skillOutput ? (
        <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
          {JSON.stringify(skillOutput, null, 2)}
        </pre>
      ) : null}
      {failure ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <span className="font-medium text-destructive">{failure.reason}: </span>
          {failure.message}
        </div>
      ) : null}
      {data ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Raw response envelope</summary>
          <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-muted p-3 font-mono">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

// ---- #3 runs panel with cost ----------------------------------------------

function RunsPanel() {
  const runs = useQuery({ queryKey: ["pg", "runs"], queryFn: listMyRuns, staleTime: 0 });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent runs (audit)</CardTitle>
      </CardHeader>
      <CardContent>
        {runs.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (runs.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No runs recorded yet — every turn appears here with tokens &amp; cost.
          </p>
        ) : (
          <div className="space-y-2">
            {(runs.data ?? []).map((r) => (
              <div
                key={r.run_id}
                className="flex items-center justify-between border-b border-border pb-2 text-sm last:border-0"
              >
                <div className="min-w-0">
                  <span className="font-mono text-xs text-muted-foreground">
                    {r.run_id.slice(0, 8)}
                  </span>{" "}
                  <span className="capitalize">{r.run_kind}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">{r.model}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {formatDate(r.started_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {r.cache_hit ? (
                    <Badge
                      variant="success"
                      className="gap-1"
                      title="Served from the semantic cache"
                    >
                      <Zap className="h-3 w-3" /> cache
                    </Badge>
                  ) : null}
                  {r.tokens_total != null ? (
                    <span className="text-xs text-muted-foreground">{r.tokens_total} tok</span>
                  ) : null}
                  {r.embedding_tokens > 0 ? (
                    <span className="text-xs text-muted-foreground" title="Embedding tokens">
                      +{r.embedding_tokens} emb
                    </span>
                  ) : null}
                  {r.tool_llm_tokens_prompt + r.tool_llm_tokens_completion > 0 ? (
                    <span className="text-xs text-muted-foreground" title="KB SQL-writing tokens">
                      +{r.tool_llm_tokens_prompt + r.tool_llm_tokens_completion} sql
                    </span>
                  ) : null}
                  {r.cost_usd != null ||
                  r.embedding_cost_usd != null ||
                  r.tool_llm_cost_usd != null ? (
                    <span
                      className="font-mono text-xs"
                      title={`Agent LLM ${formatUsd(r.cost_usd)} · KB SQL ${formatUsd(r.tool_llm_cost_usd)} · embeddings ${formatUsd(r.embedding_cost_usd)}`}
                    >
                      {formatUsd(sumCosts(r.cost_usd, r.tool_llm_cost_usd, r.embedding_cost_usd))}
                    </span>
                  ) : null}
                  <Badge variant={r.phase === "completed" ? "success" : "destructive"}>
                    {r.failure_reason ?? r.phase}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <p className="whitespace-pre-wrap text-sm text-foreground">
        {value?.trim() ? value : <span className="text-muted-foreground">—</span>}
      </p>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

/** A skill's answer: `output` on the canonical skill envelope (defect #10's
 * shape — `{run_id, status, output, failure, usage}`). It is an object, so
 * `pickText` (which looks for text) never finds it; skill answers are rendered
 * from here instead. */
function pickOutput(env: RunEnvelope): Record<string, unknown> | null {
  const v = env["output"];
  if (v && typeof v === "object" && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    return Object.keys(o).length > 0 ? o : null;
  }
  return null;
}

/** A failed skill's reason, so it reads as a failure rather than an empty panel. */
function pickFailure(env: RunEnvelope): { reason: string; message: string } | null {
  const v = env["failure"];
  if (v && typeof v === "object" && !Array.isArray(v)) {
    const f = v as Record<string, unknown>;
    return {
      reason: typeof f.reason === "string" ? f.reason : "failed",
      message: typeof f.message === "string" ? f.message : "The skill did not complete.",
    };
  }
  return null;
}

function pickText(env: RunEnvelope): string | null {
  for (const key of ["final_response", "output", "response", "content", "message"]) {
    const v = env[key];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}
